import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getStateById } from "../../redux/thunk";
import { clearSelectedState } from "../../redux/warehouse/warehouseSlice";
import EditStoreModal from "./EditStoreModal";
import { WarehouseStore } from "../../types/types";
import AddStoreToStateModal from "./AddStoreToStateModal";

const getInitials = (name: string): string => {
  if (!name) return "?";
  const words = name.split(" ");
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return (
    words[0][0] + (words.length > 1 ? words[words.length - 1][0] : "")
  ).toUpperCase();
};

export default function WareHouseList() {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingStoreData, setEditingStoreData] =
    useState<WarehouseStore | null>(null);
  const [isAddStoreModalOpen, setIsAddStoreModalOpen] = useState(false);
  const { stateId } = useParams<{ stateId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { selectedState, selectedStateStatus, selectedStateError } =
    useAppSelector((store) => store.warehouse);

  const storesToList = selectedState?.wareHouse || [];

  useEffect(() => {
    if (stateId) {
      dispatch(getStateById(stateId));
    }

    return () => {
      dispatch(clearSelectedState());
    };
  }, [stateId, dispatch]);

  let titleStateName: React.ReactNode = "..."; // Default placeholder

  if (selectedStateStatus === "loading") {
    // More descriptive loading placeholder, maybe smaller font or italics
    titleStateName = (
      <span className="italic text-gray-500 text-lg">Loading...</span>
    );
  } else if (selectedStateStatus === "succeeded" && selectedState) {
    titleStateName = selectedState.state; // Show the actual name
  } else if (selectedStateStatus === "failed") {
    titleStateName = <span className="text-red-500 text-lg">Error</span>; // Indicate error
  } else if (!stateId) {
    titleStateName = "Invalid State"; // Handle missing ID case
  }

  const handleAddStore = () => {
    navigate("/dashboard/warehouse-management/add-store");
  };

  const handleAddStoreToState = () => {
    setIsAddStoreModalOpen(true);
  };

  const handleEditStore = (store: WarehouseStore) => {
    setEditingStoreData(store);
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setEditingStoreData(null);
  };
  const handleCloseAddModal = () => {
    setIsAddStoreModalOpen(false);
  };

  let content;

  if (selectedStateStatus === "loading") {
    content = (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#008B50]"></div>
      </div>
    );
  } else if (selectedStateStatus === "succeeded" && selectedState) {
    content = (
      <div className="space-y-4 md:space-y-6">
        {storesToList.length > 0 ? (
          storesToList.map((store) => (
            <div
              onClick={() =>
                navigate(`/dashboard/store-detail/${store._id}`, {
                  state: { 
                    stateId, 
                    name: store.storeName,
                    location: store.location 
                  },
                })
              }
              key={store._id}
              className="bg-white rounded-lg shadow-md p-4 flex items-center transition duration-150 ease-in-out hover:shadow-lg w-full text-left"
            >
              <div className="w-16 h-16 bg-gradient-to-b from-[#00A662] to-[#008B50] rounded-md flex items-center justify-center text-white font-bold text-2xl mr-4 flex-shrink-0">
                {store.storeName ? getInitials(store.storeName) : "??"}
              </div>

              <div className="flex-1 space-y-1 min-w-0">
                <h3 className="text-base md:text-lg font-semibold text-gray-800 truncate">
                  {store.storeName}
                </h3>

                {store.location && (
                  <p className="text-sm text-gray-600 truncate">
                    {store.location}
                  </p>
                )}
                {store.contactInfo && (
                  <p className="text-sm text-gray-600 truncate">
                    {store.contactInfo}{" "}
                  </p>
                )}
              </div>

              <button
                className="ml-4 px-4 py-1 text-[#008B50] border border-[#008B50] rounded-md text-sm font-medium hover:bg-[#F0FDF4] transition duration-150 ease-in-out flex-shrink-0"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleEditStore(store);
                }}
              >
                Edit
              </button>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <p className="text-gray-500">
              No stores found for{" "}
              {selectedState?.state || `State ID: ${stateId}`}.
              <button
                onClick={handleAddStore}
                className="text-[#008B50] font-medium hover:underline ml-1"
              >
                Add a new store
              </button>{" "}
              to get started.
            </p>
          </div>
        )}
      </div>
    );
  } else if (selectedStateStatus === "failed") {
    content = (
      <div className="text-center text-red-600 bg-red-50 p-4 rounded-md">
        Error loading details: {selectedStateError || "Unknown error"}
        <button
          onClick={() => {
            if (stateId) dispatch(getStateById(stateId));
          }}
          className="ml-2 text-sm font-medium text-blue-600 hover:underline"
        >
          Retry
        </button>
      </div>
    );
  } else {
    content = (
      <div className="text-center text-gray-500 p-4">
        Loading store details...
      </div>
    );
  }

  return (
    <div className="px-4 md:px-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl font-semibold text-gray-800 truncate">
          Manage Store - {titleStateName} State
        </h1>
        <button
          className="bg-[#008B50] text-white px-5 py-1.5 rounded text-sm font-medium hover:bg-[#007A48] transition duration-150 ease-in-out"
          onClick={handleAddStoreToState}
        >
          Add Warehouse
        </button>
      </div>

      {content}

      <EditStoreModal
        isOpen={isEditModalOpen}
        onClose={handleCloseModal}
        storeData={editingStoreData}
        stateId={stateId}
      />

      <AddStoreToStateModal
        isOpen={isAddStoreModalOpen}
        onClose={handleCloseAddModal}
        stateId={stateId}
      />
    </div>
  );
}
